import { useState } from 'react';
import {
  IconSearch,
  IconTrash,
  IconTrashOff,
  IconFolder,
  IconFileText,
  IconPhoto,
  IconVideo,
  IconFileZip,
  IconRestore,
  IconAlertCircle,
} from '@tabler/icons-react';
import { ActionIcon, Alert, Box, Button, Card, Group, Stack, Table, Text, TextInput, Title } from '@/components/ui';
import { getTrashedFiles, formatFileSize, FileItem } from '../mockData/files';

const getFileIcon = (type: FileItem['type']) => {
  switch (type) {
    case 'folder': return IconFolder;
    case 'image': return IconPhoto;
    case 'video': return IconVideo;
    case 'archive': return IconFileZip;
    default: return IconFileText;
  }
};

const getFileColor = (type: FileItem['type']) => {
  switch (type) {
    case 'folder': return 'blue';
    case 'image': return 'green';
    case 'video': return 'red';
    case 'pdf': return 'red';
    case 'document': return 'blue';
    case 'spreadsheet': return 'green';
    case 'presentation': return 'orange';
    case 'archive': return 'gray';
    case 'code': return 'violet';
    default: return 'gray';
  }
};

const formatDate = (date: Date) => {
  return new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric', year: 'numeric' }).format(date);
};

export default function TrashPage() {
  const [search, setSearch] = useState('');
  const trashedFiles = getTrashedFiles();

  const filteredFiles = trashedFiles.filter(file =>
    file.name.toLowerCase().includes(search.toLowerCase())
  );

  // Sort by modified date descending (most recently trashed first)
  const sortedFiles = [...filteredFiles].sort((a, b) => b.modifiedAt.getTime() - a.modifiedAt.getTime());

  const totalSize = trashedFiles.reduce((acc, f) => acc + (f.size || 0), 0);

  return (
    <Stack gap="lg">
      {/* Header */}
      <Group justify="space-between">
        <Box>
          <Title order={2}>Trash</Title>
          <Text c="dimmed" size="sm">{trashedFiles.length} items ({formatFileSize(totalSize)})</Text>
        </Box>
        <Button variant="light" color="red" leftSection={<IconTrash size={16} />} disabled={trashedFiles.length === 0}>
          Empty Trash
        </Button>
      </Group>

      {/* Warning Alert */}
      {trashedFiles.length > 0 && (
        <Alert icon={<IconAlertCircle size={16} />} color="orange" variant="light">
          Items in trash will be automatically deleted after 30 days
        </Alert>
      )}

      {/* Search */}
      <Card>
        <TextInput
          placeholder="Search trash..."
          leftSection={<IconSearch size={16} />}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </Card>

      {/* Trashed Files List */}
      <Card>
        {sortedFiles.length === 0 ? (
          <Box ta="center" py="xl">
            <IconTrashOff size={48} color="var(--mantine-color-dimmed)" />
            <Text size="sm" c="dimmed" mt="md">Trash is empty</Text>
          </Box>
        ) : (
          <Table>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Name</Table.Th>
                <Table.Th>Original Location</Table.Th>
                <Table.Th>Trashed</Table.Th>
                <Table.Th>Size</Table.Th>
                <Table.Th w={100}>Actions</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {sortedFiles.map(file => {
                const Icon = getFileIcon(file.type);
                const color = getFileColor(file.type);
                return (
                  <Table.Tr key={file.id}>
                    <Table.Td>
                      <Group gap="sm">
                        <Box
                          style={{
                            width: 32,
                            height: 32,
                            borderRadius: 6,
                            backgroundColor: `var(--mantine-color-${color}-light)`,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            opacity: 0.6,
                          }}
                        >
                          <Icon size={16} color={`var(--mantine-color-${color}-filled)`} />
                        </Box>
                        <Text size="sm" c="dimmed">{file.name}</Text>
                      </Group>
                    </Table.Td>
                    <Table.Td>
                      <Text size="sm" c="dimmed">{file.path}</Text>
                    </Table.Td>
                    <Table.Td>
                      <Text size="sm" c="dimmed">{formatDate(file.modifiedAt)}</Text>
                    </Table.Td>
                    <Table.Td>
                      <Text size="sm" c="dimmed">{file.size ? formatFileSize(file.size) : '-'}</Text>
                    </Table.Td>
                    <Table.Td>
                      <Group gap="xs">
                        <ActionIcon variant="light" color="blue" size="sm" title="Restore">
                          <IconRestore size={14} />
                        </ActionIcon>
                        <ActionIcon variant="light" color="red" size="sm" title="Delete permanently">
                          <IconTrash size={14} />
                        </ActionIcon>
                      </Group>
                    </Table.Td>
                  </Table.Tr>
                );
              })}
            </Table.Tbody>
          </Table>
        )}
      </Card>
    </Stack>
  );
}
